import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Button,
  Col,
  Container,
  Row,
  OverlayTrigger,
  Tooltip,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getAccountsByPracticeNumbers, getUserPracticeById } from "../../api";
import {
  getStatementToExport,
  getAgedBalancesByAccount,
  getBankingDetailsByPracticeNumbers,
  decryptField,
  sendStatementByEmail,
} from "../../afinanceapi";
import AccountSearch from "../SearchAccounts/SearchAccounts";
import PatientSearch from "../SearchPatients/SearchPatients";
import { getAccountByPatientId } from "../../api";
import { useNavigate } from "react-router-dom";
import CreateReceiptModal from "./Receipt";
import CreditNote from "./CreditNote";
import BulkStatementModal from "../BulkStatements/BulkStatementModal";
import handleExportPDF from "./ExportStatementPDF";
import EmailStatementModal from "./EmailStatement";
import "./AccountList.css";

export default function AccountList() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / limit) || 1;

  // sorting (now server-side)
  const [sortConfig, setSortConfig] = useState({
    key: "accountOutstandingAmount",
    direction: "desc",
  });

  // data for filters
  const [practice, setPractice] = useState([]);
  const [userPracticeNumbers, setUserPracticeNumbers] = useState([]);

  // committed filters (drive the API call)
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedAccountTypes, setSelectedAccountTypes] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountData, setSelectedAccountData] = useState(null);

  // temp filters (edited inside panel)
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpAccountTypes, setTmpAccountTypes] = useState([]);

  // UI
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [userRole, setUserRole] = useState(false);
  const [showBulkStatementModal, setShowBulkStatementModal] = useState(false);
  const [statementLoadingAction, setStatementLoadingAction] = useState(null); // { id, action: "download"|"email" }
  const [showStatementEmailModal, setShowStatementEmailModal] = useState(false);
  const [statementBlob, setStatementBlob] = useState(null);
  const [statementData, setStatementData] = useState(null);
  const [statementEmailAccount, setStatementEmailAccount] = useState(null);

  const [searchMode, setSearchMode] = useState("account"); // "account" | "patient"

  const navigate = useNavigate();
  const isInitialMount = useRef(true);

  // Fetch accounts with server-side filtering, sorting, and pagination
  const fetchAccounts = useCallback(
    async (isInitial = false) => {
      try {
        if (isInitial) {
          setLoading(true);
        } else {
          setPageLoading(true);
        }

        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);

        // Store user practice numbers for later use
        if (userPracticeNumbers.length === 0) {
          setUserPracticeNumbers(user.practice || []);
        }

        const roles = Array.isArray(user?.role) ? user.role : [];
        const hasAllowedRole =
          roles.includes("SuperAdmin") || roles.includes("Finance");
        setUserRole(hasAllowedRole);

        const res = await getAccountsByPracticeNumbers({
          practiceNumbers: user.practice,
          page,
          limit,
          practiceFilter: selectedPractices,
          accountTypeFilter: selectedAccountTypes,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        });

        if (res && Array.isArray(res.accounts)) {
          setAccounts(res.accounts);
          setTotal(res.total || res.accounts.length);
        } else {
          setAccounts([]);
          setTotal(0);
        }

        // Fetch practices on initial load only
        if (isInitial && practice.length === 0) {
          const fetchedPractices = await getUserPracticeById(user._id);
          setPractice(Array.isArray(fetchedPractices) ? fetchedPractices : []);
        }
      } catch (err) {
        console.error("Error loading accounts:", err);
        setAccounts([]);
        setTotal(0);
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    },
    [
      page,
      limit,
      selectedPractices,
      selectedAccountTypes,
      sortConfig,
      practice.length,
      userPracticeNumbers.length,
    ],
  );

  // Initial load
  useEffect(() => {
    fetchAccounts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when page, filters, or sort changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchAccounts(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedPractices, selectedAccountTypes, sortConfig]);

  // Toggle sort (now triggers server-side sort)
  const toggleSort = (key) => {
    const newDirection =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction: newDirection });
    setPage(1); // Reset to first page when sorting changes
  };

  // Search select - stores account data for display
  const handleAccountSearchSelect = (account) => {
    if (account && account._id) {
      setSelectedAccountId(account._id);
      setSelectedAccountData(account);
    } else {
      setSelectedAccountId(null);
      setSelectedAccountData(null);
    }
  };

  // Get display accounts (either search result or server results)
  const displayAccounts =
    selectedAccountId && selectedAccountData ? [selectedAccountData] : accounts;

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="fa-solid fa-arrows-up-down text-primary ms-2" />;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  // panel helpers
  const toggleIn = (arr, val) =>
    arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpAccountTypes(selectedAccountTypes);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedAccountTypes(tmpAccountTypes);
    setShowFilters(false);
    setPage(1);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedAccountTypes([]);
    setSelectedAccountId(null);
    setSelectedAccountData(null); // Clear the stored account data
    setPage(1);
  };

  // Patient search → look up account by patient ID
  const handlePatientSearchSelect = async (patient) => {
    if (!patient || !patient._id) {
      setSelectedAccountId(null);
      setSelectedAccountData(null);
      return;
    }
    try {
      const account = await getAccountByPatientId(patient._id);
      if (account && account._id) {
        setSelectedAccountId(account._id);
        setSelectedAccountData(account);
      } else {
        setSelectedAccountId(null);
        setSelectedAccountData(null);
      }
    } catch (err) {
      console.error("Error fetching account by patient:", err);
      setSelectedAccountId(null);
      setSelectedAccountData(null);
    }
  };

  // MODIFIED: Clear search function
  const clearSearch = () => {
    setSelectedAccountId(null);
    setSelectedAccountData(null);
  };

  // Fetch statement data helper (shared by download & email)
  const fetchStatementData = async (account) => {
    const practiceNumbers = account.practiceNumber
      ? [account.practiceNumber]
      : userPracticeNumbers;

    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    const startDate = oneMonthAgo.toISOString().split("T")[0];
    const endDate = today.toISOString().split("T")[0];

    const data = await getStatementToExport(
      account._id,
      startDate,
      endDate,
      practiceNumbers,
    );

    const agedBalances = await getAgedBalancesByAccount(account._id);
    data.agedBalances = agedBalances.agedBalances;

    const rawBanking =
      await getBankingDetailsByPracticeNumbers(practiceNumbers);
    const decryptedBanking = await Promise.all(
      rawBanking.map(async (entry) => ({
        ...entry,
        bank: {
          ...entry.bank,
          accountNumber: entry.bank?.accountNumber
            ? await decryptField(entry.bank.accountNumber)
            : "",
          accountName: entry.bank?.accountName
            ? await decryptField(entry.bank.accountName)
            : "",
        },
      })),
    );
    data.banking = decryptedBanking;

    return data;
  };

  const handleDownloadStatement = async (e, account) => {
    e.stopPropagation();
    setStatementLoadingAction({ id: account._id, action: "download" });
    try {
      const data = await fetchStatementData(account);
      const download = handleExportPDF(data);
      await download();
    } catch (error) {
      console.error("Error downloading statement:", error);
      alert("Failed to download statement. Please try again.");
    } finally {
      setStatementLoadingAction(null);
    }
  };

  const handleEmailStatement = async (e, account) => {
    e.stopPropagation();
    setStatementLoadingAction({ id: account._id, action: "email" });
    try {
      const data = await fetchStatementData(account);
      const pdfGenerator = handleExportPDF(data);
      const blob = await pdfGenerator({ asBlob: true });

      setStatementData(data);
      setStatementBlob(blob);
      setStatementEmailAccount(account);
      setShowStatementEmailModal(true);
    } catch (error) {
      console.error("Error preparing email statement:", error);
      alert("Failed to prepare email statement. Please try again.");
    } finally {
      setStatementLoadingAction(null);
    }
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedAccountTypes.length ? 1 : 0) +
    (selectedAccountId ? 1 : 0);

  return (
    <>
      {/* Top Bar: Search + Filter + Clear + Pagination */}
      <Container className="mb-2">
        <Row className="align-items-center g-2">
          <Col xs={12} md={5}>
            <Row className="align-items-center g-2">
              {/* Switch button + arrows */}
              <Col xs="auto" className="d-flex align-items-center pe-0">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {searchMode === "account"
                        ? "Search by Patient"
                        : "Search by Account"}
                    </Tooltip>
                  }
                >
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setSearchMode((m) =>
                        m === "account" ? "patient" : "account",
                      );
                      clearSearch();
                    }}
                    className="p-0 d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 38 }}
                    aria-label="Switch search mode"
                  >
                    <i
                      className={
                        searchMode === "account"
                          ? "fa-light fa-user-magnifying-glass"
                          : "fa-light fa-file-magnifying-glass"
                      }
                    />
                  </Button>
                </OverlayTrigger>

                <span
                  className="mx-1 d-flex align-items-center"
                  aria-hidden="true"
                >
                  <i className="fa-light fa-arrow-left-arrow-right fs-6 text-primary" />
                </span>
              </Col>

              {/* Search box – give it real space */}
              <Col className="d-flex align-items-center flex-grow-1 px-0">
                <div className="w-100" style={{ minWidth: 260 }}>
                  {searchMode === "account" ? (
                    <AccountSearch
                      onAccountSelect={handleAccountSearchSelect}
                    />
                  ) : (
                    <PatientSearch
                      onPatientSelect={handlePatientSearchSelect}
                    />
                  )}
                </div>
              </Col>

              {/* Reset button – true auto width */}
              <Col xs="auto" className="d-flex justify-content-end ps-0">
                {selectedAccountId && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="p-0 d-flex align-items-center justify-content-center"
                    style={{ width: 38, height: 38 }}
                    onClick={clearSearch}
                    title="Reset"
                  >
                    <i className="fa-solid fa-rotate-right" />
                  </Button>
                )}
              </Col>
            </Row>
          </Col>

          <Col
            xs={12}
            md
            className="d-flex justify-content-md-end gap-2 mt-2 mt-md-0"
          >
            {!selectedAccountId && (
              <>
                <Button
                  variant="primary"
                  onClick={openFilters}
                  style={{ width: "120px" }}
                >
                  <i className="fa-solid fa-filter me-2" />
                  Filter{" "}
                  {activeCount > 0 && (
                    <Badge bg="light" text="dark" className="ms-2">
                      {activeCount}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={clearFilters}
                  disabled={!activeCount}
                  style={{ width: "120px" }}
                >
                  <i className="fa-solid fa-trash me-2" />
                  Filters
                </Button>
              {userRole && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowBulkStatementModal(true)}
                    style={{ width: "120px" }}
                  >
                    <i className="fa-light fa-envelopes me-2" />
                    Statements
                  </Button> 
                )} 
                <Button
                  disabled={page === 1 || pageLoading}
                  onClick={() => setPage((p) => p - 1)}
                  className="ms-2"
                  variant="primary"
                >
                  {pageLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="fa-solid fa-angle-left" />
                  )}
                </Button>
                <div className="text-nowrap small d-flex align-items-center">
                  <span className="mx-2">
                    Page {page} of {totalPages}
                  </span>
                </div>
                <Button
                  disabled={page === totalPages || pageLoading}
                  onClick={() => setPage((p) => p + 1)}
                  variant="primary"
                >
                  {pageLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="fa-solid fa-angle-right" />
                  )}
                </Button>
              </>
            )}
          </Col>
        </Row>
      </Container>

      {/* Table Container with Loading Overlay */}
      <Container style={{ position: "relative" }}>
        {/* Loading Overlay for Page Transitions */}
        {pageLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 15,
              right: 15,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "5px",
            }}
          >
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-primary">Loading page {page}...</div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading accounts...</p>
          </div>
        ) : displayAccounts.length === 0 ? (
          <p>No accounts found.</p>
        ) : (
          <Table
            bordered
            hover
            responsive
            style={{
              opacity: pageLoading ? 0.5 : 1,
              transition: "opacity 0.2s ease-in-out",
            }}
          >
            <thead className="table-primary text-center fixed-table">
              <tr>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "2%" }}
                >
                  #
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "10%" }}
                >
                  Account #
                </th>
                <th
                  className="text-center align-middle"
                  style={{ width: "18%" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-primary">Account Name</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2"
                      onClick={() => toggleSort("accountLastName")}
                    >
                      {renderSortIcon("accountLastName")}
                    </Button>
                  </div>
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "10%" }}
                >
                  ID Number
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "15%" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-primary">Medical Aid</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2"
                      onClick={() => toggleSort("accountMedicalAid")}
                    >
                      {renderSortIcon("accountMedicalAid")}
                    </Button>
                  </div>
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "15%" }}
                >
                  Plan | Option
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "10%" }}
                >
                  Medical Aid #
                </th>
                <th
                  className="text-center align-middle text-primary"
                  style={{ width: "5%" }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <span>Balance</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-2"
                      onClick={() => toggleSort("accountOutstandingAmount")}
                    >
                      {renderSortIcon("accountOutstandingAmount")}
                    </Button>
                  </div>
                </th>
                {userRole && (
                  <th
                    className="text-center align-middle text-primary"
                    style={{ width: "10%" }}
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {displayAccounts.map((account, idx) => (
                <tr
                  key={account._id}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/accounts/${account._id}`)}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#6c757d")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "")
                  }
                >
                  <td>
                    {selectedAccountId ? 1 : (page - 1) * limit + idx + 1}
                  </td>
                  <td>{account.accountNumber}</td>
                  <td>
                    {account.accountLastName}, {account.accountFullNames}
                  </td>
                  <td>{account.accountIdNumber}</td>
                  <td>
                    {account.accountMedicalAid
                      ? account.accountMedicalAid
                      : account.accountIODFund
                        ? account.accountIODFund
                        : "Private"}
                  </td>

                  <td>
                    {account.accountMedicalAid ? (
                      <>
                        {account.accountMedicalAidPlan}
                        {account.accountMedicalAidOption
                          ? ` | ${account.accountMedicalAidOption}`
                          : ""}
                      </>
                    ) : account.accountIODFund ? (
                      <>
                        {account.accountIODPlan || ""}
                        {account.accountIODOption
                          ? ` | ${account.accountIODOption}`
                          : ""}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td>{account.accountMedicalAidNumber}</td>
                  <td className="text-end">
                    {account.accountOutstandingAmount
                      ? `R${Number(account.accountOutstandingAmount).toFixed(
                          2,
                        )}`
                      : "R0.00"}
                  </td>
                  {userRole && (
                    <td
                      className="text-center align-middle"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <div className="d-flex justify-content-center align-items-center gap-0">
                        {[
                          {
                            icon: "fa-file-invoice-dollar",
                            label: "Invoice",
                            onClick: (e) => {
                              e.stopPropagation();
                              navigate(`/invoices/1/${account._id}`);
                            },
                          },
                          {
                            icon: "fa-receipt",
                            label: "Receipt",
                            onClick: (e) => {
                              e.stopPropagation();
                              setSelectedAccount(account);
                              setShowReceiptModal(true);
                            },
                          },
                          {
                            icon: "fa-light fa-undo",
                            label: "Credit Note",
                            onClick: (e) => {
                              e.stopPropagation();
                              setSelectedAccount(account);
                              setShowCreditNoteModal(true);
                            },
                          },
                          {
                            icon: "fa-download",
                            label: "Download Statement",
                            onClick: (e) => handleDownloadStatement(e, account),
                            loading:
                              statementLoadingAction?.id === account._id &&
                              statementLoadingAction?.action === "download",
                          },
                          {
                            icon: "fa-envelope",
                            label: "Email Statement",
                            onClick: (e) => handleEmailStatement(e, account),
                            loading:
                              statementLoadingAction?.id === account._id &&
                              statementLoadingAction?.action === "email",
                          },
                        ].map((btn, i) => (
                          <OverlayTrigger
                            key={i}
                            placement="top"
                            overlay={
                              <Tooltip id={`tooltip-${btn.icon}`}>
                                {btn.label}
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="link"
                              className="text-primary text-decoration-none p-0 m-0 d-flex align-items-center justify-content-center"
                              style={{ width: 24, height: 24 }}
                              onClick={(e) => btn.onClick(e)}
                              disabled={btn.loading}
                            >
                              {btn.loading ? (
                                <Spinner size="sm" />
                              ) : (
                                <i className={`fa-solid ${btn.icon} fs-6`} />
                              )}
                            </Button>
                          </OverlayTrigger>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Container>

      {/* Filter Offcanvas */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="text-primary d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() =>
                      setTmpPractices(
                        practice.map((p) => p.practiceNumber).filter(Boolean),
                      )
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 260, overflowY: "auto" }}
                >
                  {practice.length > 0 ? (
                    practice.map((p) => (
                      <div key={p.practiceNumber} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input text-primary"
                          id={`practice-${p.practiceNumber}`}
                          checked={tmpPractices.includes(p.practiceNumber)}
                          onChange={() =>
                            setTmpPractices((arr) =>
                              toggleIn(arr, p.practiceNumber),
                            )
                          }
                        />
                        <label
                          className="form-check-label text-primary"
                          htmlFor={`practice-${p.practiceNumber}`}
                        >
                          {p.practiceName}
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted mb-0">Loading practices...</p>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Account Type */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Account Type</Accordion.Header>
              <Accordion.Body>
                {["Medical Aid", "IOD", "Private"].map((type) => (
                  <div key={type} className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input text-primary"
                      id={`type-${type}`}
                      checked={tmpAccountTypes.includes(type)}
                      onChange={() =>
                        setTmpAccountTypes((arr) => toggleIn(arr, type))
                      }
                    />
                    <label
                      className="form-check-label text-primary"
                      htmlFor={`type-${type}`}
                    >
                      {type}
                    </label>
                  </div>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              variant="outline-danger"
              style={{ width: "120px" }}
              onClick={() => {
                setTmpPractices([]);
                setTmpAccountTypes([]);
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Filters
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Receipt Modal */}
      {showReceiptModal && selectedAccount && (
        <CreateReceiptModal
          show={showReceiptModal}
          onHide={() => setShowReceiptModal(false)}
          account={selectedAccount}
        />
      )}
      {showCreditNoteModal && selectedAccount && (
        <CreditNote
          show={showCreditNoteModal}
          onHide={() => setShowCreditNoteModal(false)}
          account={selectedAccount}
        />
      )}

      {/* Email Statement Modal */}
      {showStatementEmailModal && statementBlob && statementEmailAccount && (
        <EmailStatementModal
          show={showStatementEmailModal}
          onHide={() => setShowStatementEmailModal(false)}
          defaultEmail={statementEmailAccount.accountEmail}
          email2={statementEmailAccount.accountEmail2}
          email3={statementEmailAccount.accountEmail3}
          email4={statementEmailAccount.accountEmail4}
          email5={statementEmailAccount.accountEmail5}
          statementData={statementData}
          pdfBlob={statementBlob}
          sendStatementByEmail={sendStatementByEmail}
        />
      )}

      {/* Bulk Statement Modal */}
      <BulkStatementModal
        show={showBulkStatementModal}
        onHide={() => setShowBulkStatementModal(false)}
        practiceNumbers={userPracticeNumbers}
        onRunCreated={() => {}}
      />
    </>
  );
}
